<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>{{ $spinnerText }}</span>
            </span>
        </div>
    </Transition>

    <v-form :name="props.formProps.formName">
        <Tabs value="0"
              scrollable>

            <TabList class="mb-4">
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Cost allocation') }}</Tab>
                <Tab value="2">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormInputText fieldName="cost_number"
                                       label="Document number"
                                       size="2"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.cost_number" />
                        <FormSelect fieldName="contractor_id"
                                    label="Contractor"
                                    size="4"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.contractors"
                                    v-model:field="crud.contractor_id" />

                        <FormSelect fieldName="status_id"
                                    label="Status"
                                    size="3"
                                    validation="required"
                                    :isFormDisabled="true"
                                    :options="select?.statuses"
                                    v-model:field="crud.status_id" />
                        <FormSelect fieldName="creator_id"
                                    label="Creator"
                                    size="3"
                                    validation="required"
                                    :isFormDisabled="true"
                                    :options="select?.users"
                                    v-model:field="crud.creator_id" />
                    </div>
                    <div class="grid">
                        <FormCalendar fieldName="issue_date"
                                      label="Date of issue"
                                      size="2"
                                      validation="required"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.issue_date" />

                        <FormCalendar fieldName="sale_date"
                                      label="Sale date"
                                      size="2"
                                      validation="required"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.sale_date" />

                        <FormCalendar fieldName="payment_date"
                                      label="Payment date"
                                      size="2"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.payment_date" />

                        <FormInputNumber fieldName="net_value"
                                         label="Net value"
                                         size="2"
                                         validation="required"
                                         :allowEmpty="false"
                                         :minFractionDigits="2"
                                         :maxFractionDigits="2"
                                         :isFormDisabled="isFormDisabled"
                                         :update="(e) => setGrossValue(e)"
                                         v-model:field="crud.net_value" />

                        <FormInputNumber fieldName="gross_value"
                                         label="Gross value"
                                         size="2"
                                         validation="required"
                                         :allowEmpty="false"
                                         :minFractionDigits="2"
                                         :maxFractionDigits="2"
                                         :isFormDisabled="isFormDisabled"
                                         v-model:field="crud.gross_value" />

                        <FormSelect fieldName="currency_id"
                                    label="Currency"
                                    size="2"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.currencies"
                                    v-model:field="crud.currency_id" />
                    </div>
                    <div class="grid">
                        <FormEditor fieldName="description"
                                    label="Description"
                                    :isFormDisabled="isFormDisabled"
                                    v-model:field="crud.description" />
                    </div>
                </TabPanel>

                <TabPanel value="1">
                    <div :name="'cost_allocations'"></div>
                    <FormRepeaterTable v-model:record="crud.cost_allocations"
                                       repeaterStyle="margin-top:13px;width:99%"
                                       name='cost_allocations'
                                       :isFormDisabled="isFormDisabled"
                                       :keys="{ 'businessarea_id': null, 'costtype_id': null, 'net_value': null, 'description': null }"
                                       :isFirstRowEmpty="false"
                                       :isCopyButton="false"
                                       :addButtonName="__('Add decree')"
                                       :addRowFunction="addCostAllocationRow"
                                       :title="[__('Business area'), __('Cost type'), __('Net value'), __('Description')]">

                        <template #repeat="slotProps">
                            <td style="width:25%;"
                                :style="slotProps.cellStyle">
                                <FormSelect fieldName="businessarea_id"
                                            size="2"
                                            validation="required"
                                            :isFormDisabled="isFormDisabled"
                                            :options="select?.businessareas"
                                            v-model:field="slotProps.record.businessarea_id" />
                            </td>
                            <td style="width:25%;"
                                :style="slotProps.cellStyle">
                                <FormSelect fieldName="costtype_id"
                                            size="2"
                                            validation="required"
                                            :isFormDisabled="isFormDisabled"
                                            :options="select?.costtypes"
                                            v-model:field="slotProps.record.costtype_id" />
                            </td>
                            <td style="width:10%"
                                :style="slotProps.cellStyle">
                                <FormInputNumber fieldName="net_value"
                                                 class="required-field"
                                                 :allowEmpty="false"
                                                 :inRepeater="true"
                                                 :minFractionDigits="2"
                                                 :maxFractionDigits="2"
                                                 v-model:field="slotProps.record.net_value" />
                            </td>
                            <td style="width:40%"
                                :style="slotProps.cellStyle">
                                <FormTextarea fieldName="description"
                                              :inRepeater="true"
                                              :rows="3"
                                              v-model:field="slotProps.record.description" />
                            </td>
                        </template>
                    </FormRepeaterTable>
                </TabPanel>

                <TabPanel v-if="crud.log"
                          value="2"
                          :pt="{ root: { class: 'form-tab-panel' } }">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         :beforeSave="beforeSave"
                         :buttonsVisibility="formProps.buttonsVisibility"
                         v-model:isFormDisabled="isFormDisabled">

                <template #after-in-edit>
                    <AlertAddButton :addAlert="addAlert" />
                </template>
            </FormButtons>
        </div>

        <AlertAdd :formProps="{ source_table: 'costs', source_id: crud.id, width: '50%' }"
                  v-model:isAddAlertDialogOpen="isAddAlertDialogOpen" />
    </v-form>

</template>

<script setup>
import { ref, onMounted } from 'vue'
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import FormInputText from '@crud/FormInputText.vue'
import FormInputNumber from '@crud/FormInputNumber.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormCalendar from '@crud/FormCalendar.vue'
import FormRepeaterTable from '@crud/FormRepeaterTable.vue'
import FormTextarea from '@crud/FormTextarea.vue'
import FormEditor from '@crud/FormEditor.vue'
import FormButtons from '@crud/FormButtons.vue'
import ChangeLog from '@crud/ChangeLog.vue'
import AlertAdd from '@app/Data/Alert/AlertAdd.vue'
import AlertAddButton from '@alert/AlertAddButton.vue'
import { __, getStoreValue, getStatusId, currentDate, screenMessage } from '@helper/utils.js'
import { validation, initFormObj } from '@pages/Helper/crud_utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
    opportunityData: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(true)
const crud = ref({})

const options = ["statuses", "contractors", "users", "costtypes", "businessareas", "currencies"];
const select = ref(null);
const isAddAlertDialogOpen = ref(false);

// -----------------------------------------------------

onMounted(async () => {
    await initFormObj({ props, crud, isLoading, options, select, paramsToSelect: { 'statuses': { 'status_category': 'STA_COST' } } })

    if (props.formProps.addMode == true) {
        crud.value.creator_id = getStoreValue('myId');
        crud.value.status_id = await getStatusId('STA_COST', 'SYM_WORKING');
        crud.value.issue_date = currentDate();
        crud.value.currency_id = select.value.currencies.find(c => c.label == 'PLN')?.value || null;
        crud.value.cost_allocations = [];
    }
})

// -----------------------------------------------------

function setGrossValue(e) {
    const value = parseFloat(e) || 0
    const grossValue = value * 1.23
    crud.value.gross_value = grossValue.toFixed(2)
}

// -----------------------------------------------------

function addCostAllocationRow(newRecord, currentRecord) {
    const totalDocumentNetValue = parseFloat(crud.value.net_value || 0)
    const totalAllocationNetValue = currentRecord.reduce((total, record) => total + parseFloat(record.net_value || 0), 0)
    const remainingNetValue = totalDocumentNetValue - totalAllocationNetValue

    newRecord.net_value = remainingNetValue > 0 ? remainingNetValue.toFixed(2) : '0.00'
}

// -----------------------------------------------------

const beforeSave = (formData) => {
    const costAllocationnTotal = (formData.cost_allocations || []).reduce((total, allocation) => total + parseFloat(allocation.net_value || 0), 0)
    if (parseFloat(formData.net_value) !== costAllocationnTotal) {
        screenMessage({ type: 'error', title: 'Quota discrepancy', message: 'The total net value of cost allocations must be equal to the net value of the cost', timeout: 10 })
        return false
    }
    return formData
}

// -----------------------------------------------------

const addAlert = async () => {
    isAddAlertDialogOpen.value = true;
}

// -----------------------------------------------------


</script>

<style lang="scss" scoped></style>
